import CategoriesList from "@/components/mailsList/CategoriesList";
import MailIcons from "@/components/mailsList/MailIcons";
import SenderAvatar from "@/components/mailsList/SenderAvatar";
import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import Email, { MailsListId } from "@/domain/entities/emails/Email";
import MailSource from "@/domain/enums/MailSource";
import SmartLabel from "@/domain/enums/SmartLabel";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { cn } from "@/lib/utils";
import { Sparkle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cardClassName, rowDividerClassName } from "./assistantClasses";
import { mailPeople, peopleLabel } from "./mailPeople";

/** Today: the time; otherwise the day and the month. */
const shortDate = (date: Date, language: string): string => {
    const value = new Date(date);
    if (Number.isNaN(value.getTime())) return "";
    const isToday = value.toDateString() === new Date().toDateString();

    return isToday
        ? value.toLocaleTimeString(language, {
              hour: "2-digit",
              minute: "2-digit",
          })
        : value.toLocaleDateString(language, {
              day: "numeric",
              month: "short",
          });
};

interface ChatMailListProps {
    mails: Email[];
    listId: MailsListId;
    /** Among `mails`, those the user sent: shown by their recipients, and opened as from Sent. */
    sentMails?: ReadonlySet<Email>;
}

/**
 * The mails of an answer, one compact line each: sender, Focus, categories and icons as in the
 * mail list, subject, AI summary on one line, date. A click opens the mail next to the
 * conversation, as from the mail list. A mail the user sent shows who it was sent to (« À … »),
 * with neither unread dot, categories nor summary: MailVista does not analyse sent mails.
 */
const ChatMailList = ({ mails, listId, sentMails }: ChatMailListProps) => {
    const { t, i18n } = useTranslation();
    const { onMailClick } = useVistassistantMail();
    const { data: userProfile } = useGetUserProfile();

    return (
        <ul
            className={cn(
                cardClassName,
                "max-h-[360px] w-full overflow-y-auto",
            )}
        >
            {mails.map((mail) => {
                const sent = sentMails?.has(mail) ?? false;
                const people = mailPeople(mail, sent);
                const avatarUser = sent
                    ? (mail.to?.[0] ?? mail.cc?.[0])
                    : mail.from;
                const summary = sent
                    ? undefined
                    : mail.aiDetails?.mailSummary?.trim();

                return (
                    <li
                        key={mail.uniqueMailvistaId || mail.id}
                        className={rowDividerClassName}
                    >
                        <button
                            className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-assistant-chip/60"
                            onClick={() =>
                                onMailClick?.(
                                    mail,
                                    sent ? MailSource.SentItems : listId,
                                )
                            }
                            type="button"
                        >
                            <span className="relative flex flex-none">
                                <SenderAvatar
                                    className="size-8"
                                    currentUserProfile={userProfile}
                                    fallbackClassName="size-8 text-xs"
                                    text={avatarUser ? undefined : "?"}
                                    user={avatarUser}
                                />
                                {/* Unread: a dot on the avatar, so that every name starts at the same place. */}
                                {!mail.isRead && !sent && (
                                    <span
                                        aria-label={t("vistassistant.unread")}
                                        className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-primary-dark"
                                        role="img"
                                    />
                                )}
                            </span>
                            <span className="flex min-w-0 flex-1 flex-col gap-1">
                                <span className="flex min-w-0 items-center gap-2">
                                    <span className="truncate text-sm font-semibold text-primary-darkest">
                                        {sent
                                            ? t("vistassistant.sentTo", {
                                                  names: peopleLabel(people),
                                              })
                                            : people[0]}
                                    </span>
                                    {mail.isFocus && !sent && (
                                        <span className="flex h-6 flex-none items-center rounded-full bg-assistant-ink px-2 text-[13px] font-semibold text-white">
                                            {t(
                                                `navbar.smartLabels.navigation.${SmartLabel.Focus}`,
                                            )}
                                        </span>
                                    )}
                                    {!sent && <CategoriesList mail={mail} />}
                                    <MailIcons mail={mail} />
                                </span>
                                <span className="truncate text-sm text-assistant-muted">
                                    {mail.subject ||
                                        t("vistassistant.noSubject")}
                                </span>
                                {summary && (
                                    <span className="mt-0.5 flex w-fit max-w-full items-center gap-2 rounded-lg bg-assistant-chip px-2.5 py-1">
                                        <Sparkle
                                            aria-hidden
                                            className="size-4 flex-none text-primary-dark"
                                            strokeWidth={1.75}
                                        />
                                        <span className="min-w-0 truncate text-sm text-primary-darkest">
                                            {summary}
                                        </span>
                                    </span>
                                )}
                            </span>
                            <span className="flex-none text-xs tabular-nums text-assistant-muted">
                                {shortDate(mail.sentDateTime, i18n.language)}
                            </span>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
};

export default ChatMailList;
